import { Component, EventEmitter, Output, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { InventoryService } from '../../services/inventory.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { debounceTime, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
	selector: 'app-inventory-searcher',
	templateUrl: './inventory-searcher.component.html',
	styleUrls: ['./inventory-searcher.component.scss']
})
export class InventorySearcherComponent implements OnInit {
	@Input() rule: any = false;
	@Input() ispromo: any = false;
	@Input() forceupdate: any = false;
	@Output() saveresults = new EventEmitter<any>();

	results: any = [];
	categories: any = false;
	productlines: any = false;
	brands: any = [];
	user: any = false;
	brand = new UntypedFormControl([]);
	productLine = new UntypedFormControl([]);
	rangestart = new UntypedFormControl('');
	rangeend = new UntypedFormControl('');
	category = new UntypedFormControl([]);
	keywords = new UntypedFormControl('');
	products: any = false;
	inventorysearching: any = false;
	selectedproducts: any = [];
	keywordresults: any = false;
	exclusions: any = [];

	constructor(public inventoryService: InventoryService, private globalSearchService: GlobalSearchService) {}

	ngOnInit(): void {
		this.inventoryService.getItemCategories().subscribe((c) => {
			this.categories = c;
		});

		this.inventoryService.getItemProductlines().subscribe((p) => {
			this.productlines = p;
		});

		this.brand.valueChanges.subscribe(() => {
			this.updateProductLines();
		});

		this.inventoryService.getMfgs().subscribe((r) => {
			this.brands = r;
		});

		this.globalSearchService.user.subscribe((r) => {
			this.user = r;
		});

		this.keywords.valueChanges
			.pipe(
				debounceTime(500),
				switchMap((v: string) => {
					if (!v || !String(v).trim()) {
						this.keywordresults = false;
						return of(null);
					}
					return this.inventoryService.lookup({ stockid: v });
				})
			)
			.subscribe((r) => {
				this.keywordresults = r || false;
			});
	}

	private indexInMulti(arr: any[] | null | undefined, val: any): number {
		if (!Array.isArray(arr)) {
			return -1;
		}
		return arr.findIndex((x) => x === val || String(x) === String(val));
	}

	private filterActive(value: any): boolean {
		if (value === null || value === undefined || value === '') {
			return false;
		}
		if (Array.isArray(value) && value.length === 0) {
			return false;
		}
		return true;
	}

	isBrandSelected(val: any): boolean {
		return this.indexInMulti(this.brand.value, val) >= 0;
	}

	toggleBrand(val: any, checked: boolean): void {
		const arr = [...(this.brand.value || [])];
		const i = this.indexInMulti(arr, val);
		if (checked && i < 0) {
			arr.push(val);
		}
		if (!checked && i >= 0) {
			arr.splice(i, 1);
		}
		this.brand.setValue(arr);
	}

	selectAllBrands(): void {
		if (!Array.isArray(this.brands) || !this.brands.length) {
			this.brand.setValue([]);
			return;
		}
		this.brand.setValue(this.brands.map((k: any) => k.manufacturer_id));
	}

	selectNoneBrands(): void {
		this.brand.setValue([]);
	}

	onBrandCheckboxChange(val: any, ev: Event): void {
		const t = ev.target as HTMLInputElement;
		this.toggleBrand(val, t.checked);
	}

	isCategorySelected(val: any): boolean {
		return this.indexInMulti(this.category.value, val) >= 0;
	}

	toggleCategory(val: any, checked: boolean): void {
		const arr = [...(this.category.value || [])];
		const i = this.indexInMulti(arr, val);
		if (checked && i < 0) {
			arr.push(val);
		}
		if (!checked && i >= 0) {
			arr.splice(i, 1);
		}
		this.category.setValue(arr);
		this.search();
	}

	selectAllCategories(): void {
		if (!Array.isArray(this.categories) || !this.categories.length) {
			this.category.setValue([]);
			return;
		}
		this.category.setValue(this.categories.map((c: any) => c.categoryid));
		this.search();
	}

	selectNoneCategories(): void {
		this.category.setValue([]);
		this.search();
	}

	onCategoryCheckboxChange(val: any, ev: Event): void {
		const t = ev.target as HTMLInputElement;
		this.toggleCategory(val, t.checked);
	}

	isProductLineSelected(val: any): boolean {
		return this.indexInMulti(this.productLine.value, val) >= 0;
	}

	toggleProductLine(val: any, checked: boolean): void {
		const arr = [...(this.productLine.value || [])];
		const i = this.indexInMulti(arr, val);
		if (checked && i < 0) {
			arr.push(val);
		}
		if (!checked && i >= 0) {
			arr.splice(i, 1);
		}
		this.productLine.setValue(arr);
		this.search();
	}

	selectAllProductLines(): void {
		if (!Array.isArray(this.productlines) || !this.productlines.length) {
			this.productLine.setValue([]);
			return;
		}
		this.productLine.setValue(this.productlines.map((p: any) => p.line_field));
		this.search();
	}

	selectNoneProductLines(): void {
		this.productLine.setValue([]);
		this.search();
	}

	onProductLineCheckboxChange(val: any, ev: Event): void {
		const t = ev.target as HTMLInputElement;
		this.toggleProductLine(val, t.checked);
	}

	addMMItem(item: any): void {
		const exists = this.selectedproducts.filter((r: any) => {
			return r.stockid == item.stockid;
		})[0];

		if (!exists) {
			const newprod = { stockid: item.stockid, description: item.description };
			this.selectedproducts.push(newprod);
			this.keywords.setValue('');
			this.search();
		}
	}

	search(): void {
		const filters = {
			manufacturers: this.brand.value,
			productids: this.products,
			productlines: this.productLine.value,
			categories: this.category.value,
			range_start: this.rangestart.value,
			range_end: this.rangeend.value,
			exclusions: this.exclusions
		};

		let proceed = false;
		for (const [, value] of Object.entries(filters)) {
			if (this.filterActive(value)) {
				proceed = true;
				break;
			}
		}

		if (this.inventorysearching) {
			this.inventorysearching.unsubscribe();
			this.inventorysearching = false;
		}

		if (!proceed) {
			this.results = [];
			return;
		}

		this.inventorysearching = this.inventoryService.getMixItems(filters).subscribe((items: any) => {
			this.results = Array.isArray(items) ? items : [];
		});
	}

	emitMM(): void {
		const filters = {
			manufacturers: this.brand.value,
			products: this.selectedproducts,
			productlines: this.productLine.value,
			categories: this.category.value,
			range_start: this.rangestart.value,
			range_end: this.rangeend.value,
			exclusions: this.exclusions
		};
		this.saveresults.emit(filters);
	}

	updateProductLines(): void {
		const data = {
			brands: this.brand.value
		};

		this.inventoryService.getBrandProductlines(data).subscribe((r: any) => {
			this.productlines = r;
			this.search();
		});
	}

	removeItem(item: any): void {
		const index = this.selectedproducts.indexOf(item);
		if (index >= 0) {
			this.selectedproducts.splice(index, 1);
			this.search();
		}
	}

	exludeItem(item: any): void {
		const index = this.results.indexOf(item);
		if (index >= 0) {
			this.results.splice(index, 1);
			this.exclusions.push(item);
		}
		this.search();
	}

	removeExclusion(item: any): void {
		const ob = this.exclusions.filter((i: any) => {
			return i.stockid == item.stockid;
		})[0];

		if (!ob) {
			return;
		}
		const index = this.exclusions.indexOf(ob);
		if (index >= 0) {
			this.exclusions.splice(index, 1);
			this.search();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['rule']) {
			const rule = changes['rule'].currentValue;
			if (!rule) {
				this.resetLocalRuleState();
				return;
			}
			this.applyFullRule(rule);
			this.search();
			return;
		}

		if (changes['forceupdate'] && !changes['forceupdate'].firstChange && this.rule) {
			this.applyExclusionsFromRule(this.rule);
			this.search();
		}
	}

	private applyExclusionsFromRule(rule: any): void {
		if (rule.exlusions?.length) {
			this.exclusions = rule.exlusions.map((ent: any) =>
				typeof ent === 'object' && ent !== null ? ent : { stockid: ent, description: '' }
			);
		} else {
			this.exclusions = [];
		}
	}

	private resetLocalRuleState(): void {
		if (this.inventorysearching) {
			this.inventorysearching.unsubscribe();
			this.inventorysearching = false;
		}
		this.results = [];
		this.selectedproducts = [];
		this.exclusions = [];
		this.keywordresults = false;
		this.keywords.setValue('', { emitEvent: false });
		this.brand.setValue([], { emitEvent: false });
		this.category.setValue([], { emitEvent: false });
		this.productLine.setValue([], { emitEvent: false });
		this.rangestart.setValue('', { emitEvent: false });
		this.rangeend.setValue('', { emitEvent: false });
	}

	private applyFullRule(rule: any): void {
		if (rule.manufactures) {
			this.brands = rule.manufactures;
		}
		if (rule.categories) {
			this.categories = rule.categories;
		}
		if (rule.product_lines) {
			this.productlines = rule.product_lines;
		}

		if (rule.selectedmanufacturers?.length) {
			const brandvalues: any[] = [];
			rule.selectedmanufacturers.forEach((r: any) => {
				const va = this.brands.filter((p: any) => p.name === r || String(p.manufacturer_id) === String(r))[0];
				if (va) {
					brandvalues.push(va.manufacturer_id);
				}
			});
			this.brand.setValue(brandvalues);
		}

		if (rule.selectedcategories?.length) {
			const catvalues: any[] = [];
			rule.selectedcategories.forEach((p: any) => {
				catvalues.push(p);
			});
			this.category.setValue(catvalues);
		}

		if (rule.selectedlines?.length) {
			const pvalues: any[] = [];
			rule.selectedlines.forEach((p: any) => {
				pvalues.push(p);
			});
			this.productLine.setValue(pvalues);
		}

		if (rule.range_start) {
			this.rangestart.setValue(rule.range_start);
		}

		if (rule.range_end) {
			this.rangeend.setValue(rule.range_end);
		}

		this.applyExclusionsFromRule(rule);

		if (rule.productssel) {
			this.selectedproducts = Array.isArray(rule.productssel) ? [...rule.productssel] : [];
		}
	}
}
